import { memo, useEffect, useMemo, useRef, useState } from 'react'
import type { ScenarioDailyStats, ScenarioDetail } from '@/types/forklift'
import {
  buildDailyTimeline,
  formatMinutes,
  formatMinutesDuration,
  RUN_FLAG,
  type DayTimeline,
} from '@/common/scenarioRunTimeline'

// 日別の「シナリオ実行時刻」タイムライン。
// 既定は X = 日付、Y = 時刻 (JST)。orientation='time-x' で軸を入れ替え、
// X = 時刻、Y = 日付 (1 日 1 行) にできる (長期間でも日ごとの帯を読みやすくする用途。
// 行数ぶん縦に伸びる)。各シナリオ実行を開始〜終了の帯で描き、
// 日ごとの稼働時間 (初回開始〜最終終了) と実行時間合計を読み取れるようにする。
// データは scenario-stats の daily[].runs (日次集計済み) なので追加の API 呼び出しは無い。
// recharts にガントチャートが無いため SVG を直接描く。レポート (PDF) でも使うので
// width を固定で渡せるようにし、未指定なら親幅にフィットさせる。

export interface TimelineLane {
  id: string
  label: string
  color: string
}

// date-x: X=日付 / Y=時刻 (既定)、time-x: X=時刻 / Y=日付
export type TimelineOrientation = 'date-x' | 'time-x'

interface ScenarioRunTimelineProps {
  daily: ScenarioDailyStats[]
  scenarios: ScenarioDetail[]
  // シナリオ ID → 表示名 / 色 (メトリクス画面の他グラフと揃える)
  scenarioLabelMap: Record<string, string>
  scenarioColorMap: Record<string, string>
  // 複数機体の合算時: 帯の色と凡例を機体 (lane) 単位にする。
  // runs は 1 機体 1 日 1 本にまとまっているので、ツールチップも機体ごとの行になる
  lanes?: TimelineLane[]
  width?: number
  // date-x では SVG の高さ。time-x では最小の高さ (日数 × 行高がこれを超えれば伸びる)
  height?: number
  // PDF 用: 文字を小さく、ホバー無し
  compact?: boolean
  orientation?: TimelineOrientation
  // 描画する日付の範囲 [開始 index, 終了 index) (PDF で日数ぶん分割して描くとき用)。
  // 時刻レンジ・サマリーは全日付から計算し、帯だけをこの範囲に絞る
  dayRange?: [number, number]
  // 期間サマリー (実行日数・平均) と凡例を描くか (分割描画では最後の 1 回だけ出す)
  showSummary?: boolean
}

const MARGIN = { top: 8, right: 8, bottom: 22, left: 40 }
const COMPACT_MARGIN = { top: 6, right: 6, bottom: 18, left: 32 }
// time-x の左余白 (日付ラベル "MM-DD" の分)
const TIME_X_LEFT = 44
const COMPACT_TIME_X_LEFT = 36
// time-x の 1 日あたりの最小行高 (px)
const ROW_MIN_H = 14
const COMPACT_ROW_MIN_H = 7
const FALLBACK_COLOR = '#9ca3af'
const MAX_TOOLTIP_RUNS = 12

export default function ScenarioRunTimeline({
  daily,
  scenarios,
  scenarioLabelMap,
  scenarioColorMap,
  lanes,
  width,
  height = 300,
  compact = false,
  orientation = 'date-x',
  dayRange,
  showSummary = true,
}: ScenarioRunTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [measuredWidth, setMeasuredWidth] = useState(0)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 })

  useEffect(() => {
    if (width != null) return
    const el = containerRef.current
    if (!el) return
    const update = () => setMeasuredWidth(el.clientWidth)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [width])

  const timeline = useMemo(
    () => buildDailyTimeline(daily, scenarios),
    [daily, scenarios]
  )

  const timeX = orientation === 'time-x'
  const svgWidth = width ?? measuredWidth
  const baseMargin = compact ? COMPACT_MARGIN : MARGIN
  const margin = timeX
    ? { ...baseMargin, left: compact ? COMPACT_TIME_X_LEFT : TIME_X_LEFT }
    : baseMargin
  const fontSize = compact ? 9 : 11
  const { hourMin, hourMax, laneCount } = timeline
  const days = dayRange
    ? timeline.days.slice(dayRange[0], dayRange[1])
    : timeline.days
  // time-x は日数ぶん縦に伸ばす (短い期間は指定の高さに収めて行を広げる)
  const rowMinH = compact ? COMPACT_ROW_MIN_H : ROW_MIN_H
  const svgHeight = timeX
    ? Math.max(height, margin.top + margin.bottom + days.length * rowMinH)
    : height
  const plotW = Math.max(0, svgWidth - margin.left - margin.right)
  const plotH = Math.max(0, svgHeight - margin.top - margin.bottom)
  // 日付方向の 1 日あたりの幅 (date-x は横、time-x は縦)
  const dayStep = days.length > 0 ? (timeX ? plotH : plotW) / days.length : 0

  const hasRuns = timeline.days.some((d) => d.segments.length > 0)
  const hoverDay = hoverIndex != null ? days[hoverIndex] : null

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (compact || dayStep <= 0) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const idx = Math.floor((timeX ? y - margin.top : x - margin.left) / dayStep)
    if (idx < 0 || idx >= days.length) {
      setHoverIndex(null)
      return
    }
    setHoverIndex(idx)
    setHoverPos({ x, y })
  }

  return (
    <div ref={containerRef} className="relative w-full">
      {svgWidth > 0 && (
        <svg
          width={svgWidth}
          height={svgHeight}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIndex(null)}
        >
          {hoverIndex != null &&
            (timeX ? (
              <rect
                x={margin.left}
                y={margin.top + dayStep * hoverIndex}
                width={plotW}
                height={dayStep}
                fill="#f3f4f6"
              />
            ) : (
              <rect
                x={margin.left + dayStep * hoverIndex}
                y={margin.top}
                width={dayStep}
                height={plotH}
                fill="#f3f4f6"
              />
            ))}
          {/* 帯と軸はホバーで再描画しないようメモ化 */}
          <PlotLayer
            days={days}
            hourMin={hourMin}
            hourMax={hourMax}
            laneCount={laneCount}
            margin={margin}
            plotW={plotW}
            plotH={plotH}
            fontSize={fontSize}
            scenarioColorMap={scenarioColorMap}
            lanes={lanes}
            timeX={timeX}
          />
        </svg>
      )}

      {!compact && hoverDay && (
        <DayTooltip
          day={hoverDay}
          x={hoverPos.x}
          y={hoverPos.y}
          containerWidth={svgWidth}
          scenarioLabelMap={scenarioLabelMap}
          scenarioColorMap={scenarioColorMap}
          lanes={lanes}
        />
      )}

      {showSummary && (
        <>
          {/* 機体の凡例 (合算時) */}
          {lanes && lanes.length > 0 && (
            <div
              className={`flex flex-wrap gap-x-3 gap-y-1 text-gray-600 ${
                compact ? 'text-[9px] mt-1' : 'text-xs mt-2'
              }`}
            >
              {lanes.map((lane) => (
                <span key={lane.id} className="flex items-center gap-1">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm"
                    style={{ backgroundColor: lane.color }}
                  />
                  {lane.label}
                </span>
              ))}
            </div>
          )}

          {/* 期間サマリー */}
          <div
            className={`flex flex-wrap gap-x-4 gap-y-1 text-gray-600 ${
              compact ? 'text-[9px] mt-1' : 'text-xs mt-2'
            }`}
          >
            <span>
              実行日数{' '}
              <span className="font-medium text-gray-900">
                {timeline.activeDays}
              </span>{' '}
              日
            </span>
            <span>
              平均稼働時間/日{' '}
              <span className="font-medium text-gray-900">
                {hasRuns ? formatMinutesDuration(timeline.avgBoundMin) : '－'}
              </span>
            </span>
            <span>
              平均実行時間/日{' '}
              <span className="font-medium text-gray-900">
                {hasRuns ? formatMinutesDuration(timeline.avgRunMin) : '－'}
              </span>
            </span>
            <span className="text-gray-400">
              {lanes
                ? '帯は機体ごとの連続実行 (60分以上空くと分割)・薄い帯は終了未観測'
                : '薄い帯は終了未観測'}
            </span>
          </div>
        </>
      )}
    </div>
  )
}

interface PlotLayerProps {
  days: DayTimeline[]
  hourMin: number
  hourMax: number
  laneCount: number
  margin: { top: number; right: number; bottom: number; left: number }
  plotW: number
  plotH: number
  fontSize: number
  scenarioColorMap: Record<string, string>
  lanes?: TimelineLane[]
  timeX: boolean
}

const PlotLayer = memo(function PlotLayer({
  days,
  hourMin,
  hourMax,
  laneCount,
  margin,
  plotW,
  plotH,
  fontSize,
  scenarioColorMap,
  lanes: laneDefs,
  timeX,
}: PlotLayerProps) {
  // 日付方向 (date-x: 横 / time-x: 縦) と時刻方向 (date-x: 縦 / time-x: 横) の長さ
  const dayAxisLen = timeX ? plotH : plotW
  const timeAxisLen = timeX ? plotW : plotH
  const dayStep = days.length > 0 ? dayAxisLen / days.length : 0
  // 複数機体合算時は 1 日の帯を機体数で分け、同時刻の実行が重ならないようにする
  const lanes = Math.max(1, laneCount)
  const barThick = Math.max(2, Math.min(dayStep * 0.7, 28 * Math.min(lanes, 3)))
  const laneGap = lanes > 1 ? 1 : 0
  const laneThick = Math.max(1, (barThick - laneGap * (lanes - 1)) / lanes)
  const span = hourMax - hourMin
  // 時刻 (分) → 時刻軸上の座標
  const minuteToPos = (min: number) =>
    (timeX ? margin.left : margin.top) +
    ((min / 60 - hourMin) / span) * timeAxisLen
  // 日付 index → 日付軸上の中心座標
  const dayCenter = (i: number) =>
    (timeX ? margin.top : margin.left) + dayStep * (i + 0.5)

  // 日付ラベルが重ならない間隔で間引く (date-x は横幅 40px、time-x は行高)
  const labelEvery =
    dayStep > 0
      ? Math.max(1, Math.ceil((timeX ? fontSize + 3 : 40) / dayStep))
      : 1
  // 時刻グリッドは表示レンジと軸の長さに応じて 1/2/3/6 時間刻み
  const pxPerHour = span > 0 ? timeAxisLen / span : 0
  const hourStep = timeX
    ? pxPerHour >= 34
      ? 1
      : pxPerHour >= 17
        ? 2
        : pxPerHour >= 12
          ? 3
          : 6
    : span > 16
      ? 3
      : span > 10
        ? 2
        : 1
  const hourTicks: number[] = []
  for (let h = hourMin; h <= hourMax; h += hourStep) hourTicks.push(h)
  if (hourTicks[hourTicks.length - 1] !== hourMax) hourTicks.push(hourMax)

  return (
    <g>
      {/* 時刻グリッドと軸ラベル */}
      {hourTicks.map((h) => {
        const p = minuteToPos(h * 60)
        return timeX ? (
          <g key={h}>
            <line
              x1={p}
              x2={p}
              y1={margin.top}
              y2={margin.top + plotH}
              stroke="#e5e7eb"
              strokeDasharray="3 3"
            />
            <text
              x={p}
              y={margin.top + plotH + fontSize + 4}
              textAnchor="middle"
              fontSize={fontSize}
              fill="#6b7280"
            >
              {`${h}:00`}
            </text>
          </g>
        ) : (
          <g key={h}>
            <line
              x1={margin.left}
              x2={margin.left + plotW}
              y1={p}
              y2={p}
              stroke="#e5e7eb"
              strokeDasharray="3 3"
            />
            <text
              x={margin.left - 4}
              y={p + fontSize / 3}
              textAnchor="end"
              fontSize={fontSize}
              fill="#6b7280"
            >
              {`${h}:00`}
            </text>
          </g>
        )
      })}

      {/* 日付ラベル */}
      {days.map((day, i) =>
        i % labelEvery === 0 ? (
          timeX ? (
            <text
              key={day.date}
              x={margin.left - 4}
              y={dayCenter(i) + fontSize / 3}
              textAnchor="end"
              fontSize={fontSize}
              fill="#6b7280"
            >
              {day.date.slice(5)}
            </text>
          ) : (
            <text
              key={day.date}
              x={dayCenter(i)}
              y={margin.top + plotH + fontSize + 4}
              textAnchor="middle"
              fontSize={fontSize}
              fill="#6b7280"
            >
              {day.date.slice(5)}
            </text>
          )
        ) : null
      )}

      {/* 実行の帯 */}
      {days.map((day, i) => {
        const base = dayCenter(i) - barThick / 2
        return day.segments.map((seg, j) => {
          const p1 = minuteToPos(seg.startMin)
          const p2 = minuteToPos(seg.endMin)
          const len = Math.max(1, p2 - p1)
          const color = laneDefs
            ? (laneDefs[seg.lane]?.color ?? FALLBACK_COLOR)
            : (scenarioColorMap[seg.scenarioId] ?? FALLBACK_COLOR)
          const lanePos =
            base + Math.min(seg.lane, lanes - 1) * (laneThick + laneGap)
          return (
            <rect
              key={`${day.date}-${j}`}
              x={timeX ? p1 : lanePos}
              y={timeX ? lanePos : p1}
              width={timeX ? len : laneThick}
              height={timeX ? laneThick : len}
              fill={color}
              fillOpacity={seg.open ? 0.35 : 0.9}
              rx={1}
            />
          )
        })
      })}

      {/* 軸線 */}
      <line
        x1={margin.left}
        x2={margin.left}
        y1={margin.top}
        y2={margin.top + plotH}
        stroke="#d1d5db"
      />
      <line
        x1={margin.left}
        x2={margin.left + plotW}
        y1={margin.top + plotH}
        y2={margin.top + plotH}
        stroke="#d1d5db"
      />
    </g>
  )
})

function DayTooltip({
  day,
  x,
  y,
  containerWidth,
  scenarioLabelMap,
  scenarioColorMap,
  lanes,
}: {
  day: DayTimeline
  x: number
  y: number
  containerWidth: number
  scenarioLabelMap: Record<string, string>
  scenarioColorMap: Record<string, string>
  lanes?: TimelineLane[]
}) {
  // 右端に近い場合はカーソルの左に出す
  const flip = x > containerWidth * 0.6
  const style: React.CSSProperties = {
    top: Math.max(0, y - 12),
    ...(flip ? { right: containerWidth - x + 12 } : { left: x + 12 }),
  }
  const shown = day.segments.slice(0, MAX_TOOLTIP_RUNS)
  const rest = day.segments.length - shown.length
  return (
    <div
      className="absolute z-10 bg-white border border-gray-200 rounded shadow-lg p-3 text-sm pointer-events-none min-w-[220px]"
      style={style}
    >
      <p className="font-medium text-gray-900 mb-1">{day.date}</p>
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-gray-700">
        <span>実行回数</span>
        <span className="text-right font-medium">{day.runCount} 回</span>
        <span>初回開始</span>
        <span className="text-right font-medium">
          {day.firstStartMin != null ? formatMinutes(day.firstStartMin) : '－'}
        </span>
        <span>最終終了</span>
        <span className="text-right font-medium">
          {day.lastEndMin != null ? formatMinutes(day.lastEndMin) : '－'}
        </span>
        <span>稼働時間</span>
        <span className="text-right font-medium">
          {day.boundMin > 0 ? formatMinutesDuration(day.boundMin) : '－'}
        </span>
        <span>実行時間合計</span>
        <span className="text-right font-medium">
          {day.runMin > 0 ? formatMinutesDuration(day.runMin) : '－'}
        </span>
      </div>
      {shown.length > 0 && (
        <div className="border-t border-gray-200 mt-2 pt-1 space-y-0.5 text-xs">
          {shown.map((seg, i) => (
            <div key={i} className="flex items-center gap-2">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                style={{
                  backgroundColor: lanes
                    ? (lanes[seg.lane]?.color ?? FALLBACK_COLOR)
                    : (scenarioColorMap[seg.scenarioId] ?? FALLBACK_COLOR),
                  opacity: seg.open ? 0.35 : 0.9,
                }}
              />
              <span className="text-gray-500 tabular-nums">
                {formatMinutes(seg.startMin)}–
                {seg.open ? '' : formatMinutes(seg.endMin)}
              </span>
              <span className="text-gray-700 truncate max-w-[160px]">
                {lanes
                  ? (lanes[seg.lane]?.label ?? '不明')
                  : (scenarioLabelMap[seg.scenarioId] ??
                    seg.scenarioId ??
                    '不明')}
              </span>
              {lanes && (
                <span className="text-gray-500 tabular-nums">
                  {seg.count} 回
                  {!seg.open && ` / ${formatMinutesDuration(seg.runMin)}`}
                </span>
              )}
              {seg.flag === RUN_FLAG.canceled && (
                <span className="text-red-500">中断</span>
              )}
              {seg.flag === RUN_FLAG.errorStopped && (
                <span className="text-orange-500">エラー</span>
              )}
            </div>
          ))}
          {rest > 0 && <p className="text-gray-400">他 {rest} 件</p>}
        </div>
      )}
    </div>
  )
}
